/* eslint-disable radix */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */
import {
  getTemplateType, initializeKetcherData, templateWithBoundingBox,
  fetchKetcherData, parsePolymerEntryByAtomIndex
} from 'src/utilities/ketcherSurfaceChemistry/InitializeAndParseKetcher';
import {
  imageNodeCounter, imageUsedCounterSetter,
  latestData,
} from 'src/components/structureEditor/KetcherEditor';
import {
  ALIAS_PATTERNS, KET_TAGS,
} from 'src/utilities/ketcherSurfaceChemistry/constants';
import {
  mols,
  textNodeStruct,
  textNodeStructSetter,
  imagesList,
  textList,
  allAtoms,
  deletedAtomsSetter
} from 'src/utilities/ketcherSurfaceChemistry/stateManager';
import {
  isAliasConsistent,
  deepCompare,
  deepCompareNumbers,
} from 'src/utilities/ketcherSurfaceChemistry/TextNode';
import {
  prepareImageFromTemplateList,
  findTemplateByPayload
} from 'src/utilities/ketcherSurfaceChemistry/Ketcher2SurfaceChemistryUtils';

// Helper to update an atom with K2SC labels and aliases
const updateAtom = (atomLocation, templateType, imageCounter) => ({
  label: KET_TAGS.inspiredLabel,
  alias: `t_${templateType}_${imageCounter}`,
  location: atomLocation
});

// Generate a unique key for text nodes (used when pasting duplicates)
const generateUniqueTextKey = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let key = '';
  for (let i = 0; i < 5; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
};

// Check if two positions are approximately the same
const isSamePosition = (pos1, pos2) => {
  if (!pos1 || !pos2) return false;
  return Math.abs(pos1.x - pos2.x) < 0.1 && Math.abs(pos1.y - pos2.y) < 0.1;
};

// Associate pasted text with new alias by finding duplicates and assigning unique keys
// When Ketcher pastes text, it duplicates the key - we need to give the pasted text a new unique key
const associateTextNodeWithNewAlias = (atomLocation, newAlias, oldAlias) => {
  if (!textList || textList.length === 0) return;

  // Find the original text's position and key
  let originalTextPosition = null;
  let originalKey = null;
  if (oldAlias && textNodeStruct[oldAlias]) {
    originalKey = textNodeStruct[oldAlias];
    for (const textNode of textList) {
      try {
        const content = JSON.parse(textNode.data.content);
        if (content.blocks[0]?.key === originalKey) {
          originalTextPosition = textNode.data?.position;
          break;
        }
      } catch (e) { /* Skip */ }
    }
  }

  // Find pasted text (same key as original but different position) and give it a unique key
  for (let i = 0; i < textList.length; i++) {
    const textNode = textList[i];
    try {
      const content = JSON.parse(textNode.data.content);
      const textKey = content.blocks[0]?.key;
      const textPos = textNode.data?.position;

      // Pasted text has same key but different position
      if (originalKey && textKey === originalKey && !isSamePosition(textPos, originalTextPosition)) {
        const newKey = generateUniqueTextKey();
        content.blocks[0].key = newKey;
        textNode.data.content = JSON.stringify(content);
        textList[i] = textNode;

        const updatedStruct = { ...textNodeStruct };
        updatedStruct[newAlias] = newKey;
        textNodeStructSetter(updatedStruct);
        return;
      }
    } catch (e) { /* Skip */ }
  }

  // Fallback: find any unassociated text at a different position
  const associatedKeys = new Set(Object.values(textNodeStruct));
  for (let i = 0; i < textList.length; i++) {
    const textNode = textList[i];
    try {
      const content = JSON.parse(textNode.data.content);
      const textKey = content.blocks[0]?.key;
      const textPos = textNode.data?.position;

      if (isSamePosition(textPos, originalTextPosition)) continue;

      // Generate unique key if already associated elsewhere
      let finalKey = textKey;
      if (associatedKeys.has(textKey)) {
        finalKey = generateUniqueTextKey();
        content.blocks[0].key = finalKey;
        textNode.data.content = JSON.stringify(content);
        textList[i] = textNode;
      }

      const updatedStruct = { ...textNodeStruct };
      updatedStruct[newAlias] = finalKey;
      textNodeStructSetter(updatedStruct);
      return;
    } catch (e) { /* Skip */ }
  }
};

// helper function to process ketcher-rails files and adding image to ketcher canvas
// List order must match atom index order (first entry = first R# atom) so shapes stay correct on import.
const addingPolymersToKetcher = async (railsPolymersList, data) => {
  try {
    // Split by whitespace and drop empties; preserve order so each R# atom gets the correct entry
    const polymerList = (railsPolymersList || '').trim().split(/\s+/).filter(Boolean);
    const collectedImages = [];
    await initializeKetcherData(data);

    // When format is atomIndex/templateId/size (e.g. "2/10/1.00-1.00 0/25/1.00-1.00 1/52/1.50-2.00"),
    // assign each entry to the correct atom by index so images and TextNode aliases stay in sync.
    const polymerByAtomIndex = {};
    let useIndexedFormat = false;
    let hasCollision = false;
    for (let i = 0; i < polymerList.length; i++) {
      const parsed = parsePolymerEntryByAtomIndex(polymerList[i]);
      if (parsed != null) {
        if (polymerByAtomIndex[parsed.atomIndex] !== undefined) {
          hasCollision = true;
        }
        polymerByAtomIndex[parsed.atomIndex] = { type: parsed.type, size: parsed.size };
        useIndexedFormat = true;
      }
    }

    if (useIndexedFormat && !hasCollision && Object.keys(polymerByAtomIndex).length > 0) {
      // imageSeqCounter is the alias third-part index (0-based sequence of images collected).
      // It must NOT be the KET atom index — placeImageOnAtoms uses it to index into imagesList.
      let imageSeqCounter = 0;
      // globalAtomIndex mirrors the save-side globalAtomOffset so polymerByAtomIndex
      // (keyed by global index) resolves correctly across multiple molecules.
      let globalAtomIndex = 0;
      for (const molName of mols) {
        const molecule = data[molName];
        if (!molecule?.atoms) continue;
        for (let localIndex = 0; localIndex < molecule.atoms.length; localIndex++) {
          const atom = molecule.atoms[localIndex];
          const aliasPass = (
            atom.type === KET_TAGS.rgLabel
            || atom.label === KET_TAGS.inspiredLabel
            || atom.label === KET_TAGS.RGroupTag
            || atom.label === 'R'
            || ALIAS_PATTERNS.threeParts.test(atom.alias)
          );
          const entry = polymerByAtomIndex[globalAtomIndex + localIndex];
          if (entry && aliasPass) {
            const { type: templateType, size: templateSize } = { type: entry.type, size: entry.size };
            data[molName].atoms[localIndex] = updateAtom(atom.location, templateType, imageSeqCounter);
            const newTemplate = await templateWithBoundingBox(templateType, atom.location, templateSize);
            if (newTemplate) collectedImages.push(newTemplate);
            imageSeqCounter++;
          }
        }
        globalAtomIndex += molecule.atoms.length;
      }
      imageUsedCounterSetter(imageSeqCounter > 0 ? imageSeqCounter - 1 : 0);
    } else {
      // Legacy: assign in consumption order (no atom index in polymer entries)
      let visitedAtoms = 0;
      for (const molName of mols) {
        const molecule = data[molName];
        if (!molecule?.atoms) continue;
        for (let atomIndex = 0; atomIndex < molecule.atoms.length; atomIndex++) {
          const atom = molecule.atoms[atomIndex];
          const polymerItem = polymerList[visitedAtoms];
          const aliasPass = (
            atom.type === KET_TAGS.rgLabel
            || atom.label === KET_TAGS.inspiredLabel
            || atom.label === KET_TAGS.RGroupTag
            || atom.label === 'R'
            || ALIAS_PATTERNS.threeParts.test(atom.alias)
          );
          if (polymerItem && aliasPass) {
            const { type: templateType, size: templateSize } = getTemplateType(polymerItem);
            imageUsedCounterSetter(imageNodeCounter + 1);
            visitedAtoms += 1;
            data[molName].atoms[atomIndex] = updateAtom(atom.location, templateType, imageNodeCounter);
            const newTemplate = await templateWithBoundingBox(templateType, atom.location, templateSize);
            if (newTemplate) collectedImages.push(newTemplate);
          }
        }
      }
    }

    return { c_images: collectedImages, molfileData: data };
  } catch (err) {
    console.error({ err: err.message });
    return { c_images: [], molfileData: data };
  }
};

// filter out mol-node from ket2 format
const removeMoleculeFromData = (data, molKey) => data.root.nodes.filter((node) => node.$ref !== molKey);

// filter images from nodes
const removeImagesFromData = (data) => data.root.nodes.filter((node) => node.type !== 'image');

// filter images from nodes
const removeTextFromData = (data) => data.root.nodes.filter((node) => node.type !== 'text');

// helper function to remove bonds by atom id
const updateBondList = async (indexList, bondList, atomList) => {
  const removedIndices = [...indexList].sort((a, b) => a - b);
  const atomCount = atomList?.length ?? 0;
  const result = [];

  for (const bond of bondList) {
    if (!bond.atoms.some((i) => removedIndices.includes(i))) {
      const adjustedAtoms = bond.atoms.map((atom) => {
        const shift = removedIndices.filter((removed) => removed < atom).length;
        return atom - shift;
      });
      if (adjustedAtoms.every((i) => i >= 0 && i < atomCount)) {
        result.push({ ...bond, atoms: adjustedAtoms });
      }
    }
  }
  return result;
};

// helper function to remove template by atom with alias
const handleOnDeleteAtom = async (missingNumbers, data, imageL) => {
  try {
    const textNodeStructureCopy = { ...textNodeStruct };
    const structureKeys = Object.keys(textNodeStruct);
    structureKeys?.forEach((i) => {
      const split = parseInt(i.split('_')[2]);
      const missingIndex = missingNumbers.indexOf(split);
      if (missingIndex !== -1) {
        delete textNodeStructureCopy[i];
      }
    });

    for (const molKey of mols) {
      const mol = data[molKey];
      if (mol && mol?.atoms) {
        for (const atom of mol.atoms) {
          if (ALIAS_PATTERNS.threeParts.test(atom?.alias)) {
            const previousAlias = atom.alias;
            const atomSplits = previousAlias.split('_');
            const currentAlias = parseInt(atomSplits[2]);

            // Count how many missing numbers are LESS than the current alias
            const missingCount = missingNumbers.filter((num) => num <= currentAlias).length;
            if (missingCount > 0) {
              let newAlias = currentAlias - missingCount;
              if (newAlias < 0) newAlias = 0;
              atom.alias = `t_${atomSplits[1]}_${newAlias}`;
              if (textNodeStructureCopy[previousAlias]) {
                textNodeStructureCopy[atom.alias] = textNodeStructureCopy[previousAlias];
                delete textNodeStructureCopy[previousAlias];
              }
            }
          }
        }
        data[molKey] = mol;
      }
    }

    textNodeStructSetter(textNodeStructureCopy);
    data.root.nodes = data.root.nodes.filter((node) => node.type !== 'image');
    data.root.nodes.push(...imageL);
    return data;
  } catch (err) {
    console.error('handleDelete!!', err.message);
    return null;
  }
};

// remove atoms from the template-list with alias,
const removeAtomFromData = async (data, aliasToBeRemoved) => {
  try {
    for (const molKey of mols) {
      const molecule = data[molKey];
      if (molecule && molecule.atoms) {
        const atomList = molecule?.atoms || [];
        const atomListCopy = molecule?.atoms || [];
        const bondList = molecule?.bonds || [];
        const removeIndexList = [];

        for (let i = 0; i < atomListCopy.length; i++) {
          if (ALIAS_PATTERNS.threeParts.test(atomListCopy[i].alias)) {
            const split = parseInt(atomListCopy[i].alias.split('_')[2]);
            if (aliasToBeRemoved.indexOf(split) !== -1) {
              atomList.splice(i, 1);
              removeIndexList.push(i);
            }
            if (!atomList?.length) {
              data.root.nodes = removeMoleculeFromData(data, molKey);
              delete data[molKey];
            }
          }
        }

        if (removeIndexList.length && bondList.length) {
          data[molKey].bonds = await updateBondList(removeIndexList, bondList, atomList);
        }
        if (atomList.length) data[molKey].atoms = atomList;
      }
    }
    return data;
  } catch (err) {
    console.error('removeImageTemplateAtom', err.message);
    return data;
  }
};

// helper function to find atom in ket format by image idx referenced with alias 3rd part
const findAtomByImageIndex = async (imgIdx) => {
  for (const molName of mols) {
    const molecule = latestData[molName];
    for (let atomIndex = 0; atomIndex < molecule.atoms.length; atomIndex++) {
      const atom = molecule.atoms[atomIndex];
      if (ALIAS_PATTERNS.threeParts.test(atom.alias)) {
        const aliasLastPart = parseInt(atom.alias.split('_')[2]);
        if (imgIdx === aliasLastPart) return { atomLocation: atom.location, alias: atom.alias };
      }
    }
  }
  return { atomLocation: null, alias: '' };
};

// helper function to collect missing alias
const collectMissingAliases = async () => {
  const aliasList = [];
  for (let i = 0; i < mols.length; i++) {
    const { atoms } = latestData[mols[i]];
    for (let j = 0; j < atoms.length; j++) {
      const split = atoms[j]?.alias?.split('_')[2];
      if (split) {
        aliasList.push(parseInt(split));
      }
    }
  }
  return aliasList;
};

// helper function set image coordinates
const adjustAtomCoordinates = (imageCoordinates) => {
  const centerX = imageCoordinates.width / 2;
  const centerY = imageCoordinates.height / 2;
  const centerZ = imageCoordinates.z || 0; // defaulting z to 0 if it's not defined
  return [imageCoordinates.x + centerX, imageCoordinates.y - centerY, centerZ];
};

/* IMP: helper function when new atom is added or rebase for alias
  -> Two parts => t_01 => will always be new template added from the template list
  -> Three parts => t_templateid_used_image_counter
    ----- possible cases
  -> two part with image -> is an event when a new template is added to canvas as new molecule
  -> two parts with no image -> is an event when a new template is directly added to other molecule
  -> three part with image -> can be a regular case when an atom with 3 three part aliases is pasted on canvas or can a saved template
  -> three part without image -> in case there the canvas is not synced an image is there
    ----- notes
  -> tbr -> flag means this atom has to removed from the list coming from the template
  -> isAliasConsistent before returning -> is a function to make sure all aliases generated are in order 0,1,2,3,4,5,6...
  */
const addAtomAliasHelper = async (processedAtoms) => {
  try {
    const newImageNodes = [...imagesList];
    imageUsedCounterSetter(processedAtoms.length - 1);

    for (let m = 0; m < mols.length; m++) {
      const mol = latestData[mols[m]];
      const removableIndices = [];

      for (let a = 0; a < mol?.atoms?.length; a++) {
        const atom = mol.atoms[a];
        const splits = atom?.alias?.split('_');

        if (ALIAS_PATTERNS.twoParts.test(atom.alias)) {
          // New template added - assign next available index
          imageUsedCounterSetter(imageNodeCounter + 1);
          if (!newImageNodes[imageNodeCounter]) {
            // eslint-disable-next-line no-await-in-loop
            const img = await prepareImageFromTemplateList(parseInt(splits[1]), atom.location);
            newImageNodes.push(img);
          }
          atom.alias += `_${imageNodeCounter}`;
          processedAtoms.push(`${m}_${a}_${imageNodeCounter}`);
        } else if (ALIAS_PATTERNS.threeParts.test(atom.alias)) {
          const checkKey = `${m}_${a}_${splits[2]}`;
          const isInProcessed = processedAtoms.indexOf(checkKey) !== -1;

          if (isInProcessed) {
            // Existing atom - create image if missing
            if (!newImageNodes[imageNodeCounter]) {
              // eslint-disable-next-line no-await-in-loop
              const img = await prepareImageFromTemplateList(parseInt(splits[1]), atom.location);
              newImageNodes.push(img);
            }
          } else {
            // Duplicate alias from paste - reassign to new unique index
            const oldAlias = atom.alias;
            imageUsedCounterSetter(imageNodeCounter + 1);
            const newAlias = `t_${splits[1]}_${imageNodeCounter}`;
            atom.alias = newAlias;
            processedAtoms.push(`${m}_${a}_${imageNodeCounter}`);

            // Create image for pasted atom
            if (!newImageNodes[imageNodeCounter]) {
              // eslint-disable-next-line no-await-in-loop
              const img = await prepareImageFromTemplateList(parseInt(splits[1]), atom.location);
              newImageNodes.push(img);
            }

            // Associate pasted text with new alias
            associateTextNodeWithNewAlias(atom.location, newAlias, oldAlias);
          }
        }

        if (atom.label === 'tbr') {
          removableIndices.push(atom);
        }
      }

      if (removableIndices.length) {
        mol.atoms?.splice(mol.atoms.length - removableIndices.length, removableIndices.length);
        mol.bonds?.splice(mol.bonds.length - removableIndices.length, removableIndices.length);
      }
    }

    const d = { ...latestData };
    const molsList = removeImagesFromData(d);
    d.root.nodes = [...molsList, ...newImageNodes];
    return { d, isConsistent: isAliasConsistent() };
  } catch (err) {
    console.error('addAtomAliasHelper', err.message);
    return { d: null, isConsistent: false };
  }
};

// IMP: helper function to handle new atoms added to the canvas
export const handleAddAtom = async () => {
  const processedAtoms = [];
  imageUsedCounterSetter(-1);
  const seenThirdParts = new Set();

  // Collect unique atoms by their alias third part (index)
  for (let m = 0; m < mols.length; m++) {
    const mol = latestData[mols[m]];
    for (let a = 0; a < mol?.atoms?.length; a++) {
      const atom = mol.atoms[a];
      if (atom?.alias && ALIAS_PATTERNS.threeParts.test(atom?.alias)) {
        const splits = atom?.alias?.split('_');
        if (!seenThirdParts.has(splits[2])) {
          processedAtoms.push(`${m}_${a}_${splits[2]}`);
          seenThirdParts.add(splits[2]);
        }
      }
    }
  }
  return addAtomAliasHelper(processedAtoms);
};

export const eventCollectDeletedAtoms = (eventItem) => {
  let atomCount = -1;
  const deletedAtomsList = [];
  if (eventItem.label === KET_TAGS.inspiredLabel) {
    for (let m = 0; m < mols?.length; m++) {
      const mol = mols[m];
      const atoms = latestData[mol]?.atoms;
      for (let a = 0; a < atoms?.length; a++) {
        atomCount++;
        if (atomCount === eventItem.id) {
          deletedAtomsList.push(allAtoms[atomCount]);
        }
      }
    }
  }
  deletedAtomsSetter([...deletedAtomsList]);
};

const deepCompareContentImages = async (oldArray, newArray) => {
  if (!oldArray.length && !newArray.length) return [];
  const missingIndexes = [];
  for (let i = 0; i < oldArray.length; i++) {
    const oldItem = oldArray[i];
    let found = false;
    for (let j = 0; j < newArray.length; j++) {
      const newItem = newArray[j];
      const isMatch = oldItem.data === newItem.data
        && oldItem.boundingBox?.x === newItem.boundingBox?.x
        && oldItem.boundingBox?.y === newItem.boundingBox?.y;
      if (isMatch) {
        found = true;
        break;
      }
    }
    if (!found) {
      missingIndexes.push(i);
    }
  }
  return missingIndexes;
};

const analyzeAliasAndImageDifferences = async (editor, oldImagePack) => {
  const listOfAliasesBefore = await collectMissingAliases();
  await fetchKetcherData(editor);

  // 1st collect difference between old and new data to know what's removed
  const listOfAliasesAfter = await collectMissingAliases();
  const aliasDifferences = await deepCompareNumbers(listOfAliasesBefore, listOfAliasesAfter);
  const hasImageDifferences = await deepCompare(oldImagePack, imagesList);
  const imageDifferences = await deepCompareContentImages(oldImagePack, imagesList);

  return {
    aliasDifferences,
    hasImageDifferences,
    imageDifferences
  };
};

const filterImagesByDifferences = async (aliasDifferences) => {
  const filteredImages = [];
  for (let i = 0; i < imagesList.length; i++) {
    if (aliasDifferences.indexOf(i) !== -1) {
      const templateId = await findTemplateByPayload(imagesList[i].data);
      if (templateId == null) {
        filteredImages.push(imagesList[i]);
      }
    } else {
      filteredImages.push(imagesList[i]);
    }
  }
  return filteredImages;
};

export {
  updateAtom,
  addingPolymersToKetcher,
  removeMoleculeFromData,
  removeImagesFromData,
  removeTextFromData,
  updateBondList,
  handleOnDeleteAtom,
  removeAtomFromData,
  findAtomByImageIndex,
  collectMissingAliases,
  adjustAtomCoordinates,
  analyzeAliasAndImageDifferences,
  filterImagesByDifferences
};
